import React from 'react';
import { motion } from 'framer-motion';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';
import { Link } from 'react-router-dom';
import {
  Users,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  Clock,
  ChefHat,
  AlertCircle,
  LayoutDashboard,
  Smartphone,
} from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { AdminDashboardGrid } from '@/features/admin/components/AdminDashboardGrid';
// Lazy load SuperAdminDashboard to prevent premature localStorage creation
const SuperAdminDashboard = React.lazy(() => import('@/features/admin/pages/SuperAdminDashboard'));
import { formatKyrgyzCurrency } from '@/lib/i18n';
import { LanguageDebugger } from '@/components/LanguageDebugger';

// Stats Card Component
const StatsCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  color = 'primary',
}: {
  title: string;
  value: string;
  /** Omit when there is no real trend to show — never invent one. */
  change?: string;
  changeType?: 'positive' | 'negative';
  icon: React.ComponentType<{ className?: string }>;
  color?: 'primary' | 'success' | 'warning' | 'danger';
}) => {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600 ring-1 ring-primary-100',
    success: 'bg-success-50 text-success-600 ring-1 ring-success-100',
    warning: 'bg-warning-50 text-warning-600 ring-1 ring-warning-100',
    danger: 'bg-danger-50 text-danger-600 ring-1 ring-danger-100',
  };

  return (
    <div data-testid="stat-card" className="bg-white rounded-xl p-6 border border-neutral-200 transition-colors duration-150 hover:border-neutral-400">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-neutral-500">{title}</p>
          <p data-testid="stat-value" className="text-4xl font-bold tracking-tight text-neutral-900 mt-2 tabular-nums">{value}</p>
          {change ? (
            <div className="flex items-center mt-2">
              <TrendingUp
                className={`h-3.5 w-3.5 mr-1 ${
                  changeType === 'positive' ? 'text-success-600' : 'text-danger-600'
                }`}
              />
              <span
                className={`text-sm font-medium tabular-nums ${
                  changeType === 'positive' ? 'text-success-600' : 'text-danger-600'
                }`}
              >
                {change}
              </span>
            </div>
          ) : (
            <div className="mt-2 text-sm text-neutral-400">—</div>
          )}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({
  title,
  description,
  icon: Icon,
  onClick,
  color = 'primary',
}: {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  color?: 'primary' | 'success' | 'warning' | 'danger';
}) => {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600 ring-1 ring-primary-100',
    success: 'bg-success-50 text-success-600 ring-1 ring-success-100',
    warning: 'bg-warning-50 text-warning-600 ring-1 ring-warning-100',
    danger: 'bg-danger-50 text-danger-600 ring-1 ring-danger-100',
  };

  return (
    <motion.button
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className="w-full p-6 rounded-xl bg-white border border-neutral-200 text-left transition-colors duration-150 hover:border-neutral-400 group"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-neutral-900 mb-0.5 truncate">{title}</h3>
            <p className="text-sm text-neutral-500 truncate">{description}</p>
          </div>
        </div>
        <span className="text-neutral-300 group-hover:text-neutral-500 transition-colors duration-150 text-lg leading-none">›</span>
      </div>
    </motion.button>
  );
};

// Recent Activity Item Component
const ActivityItem = ({
  title,
  description,
  time,
  type,
}: {
  title: string;
  description: string;
  time: string;
  type: 'order' | 'table' | 'payment';
}) => {
  const typeConfig = {
    order: { icon: ShoppingBag, color: 'bg-primary-100 text-primary-600' },
    table: { icon: Users, color: 'bg-success-100 text-success-600' },
    payment: { icon: DollarSign, color: 'bg-warning-100 text-warning-600' },
  };

  const { icon: Icon, color } = typeConfig[type];

  return (
    <div className="flex items-center space-x-4 p-4 hover:bg-neutral-50 rounded-xl transition-colors">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-900 truncate">{title}</p>
        <p className="text-xs text-neutral-500">{description}</p>
      </div>
      <div className="text-xs text-neutral-400">{time}</div>
    </div>
  );
};

export const DashboardPage: React.FC = () => {
  const { t, i18n } = useSafeTranslation();
  const { user } = useAuthStore();
  
  // Debug language
  console.log('Dashboard language:', i18n.language);
  console.log('Available languages:', i18n.languages);
  console.log('Translation test:', t('dashboard.welcomeBack'));
  
  // Detect mobile screens
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Show super admin dashboard for super admin users
  if (user?.role === 'super_admin') {
    return (
      <React.Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      }>
        <SuperAdminDashboard />
      </React.Suspense>
    );
  }

  // Show admin dashboard for admin users
  if (user?.role === 'restaurant_owner') {
    return (
      <div className="space-y-6">
        {/* PROMINENT MENU MANAGEMENT BANNER */}
        <div className="relative overflow-hidden bg-neutral-900 rounded-2xl p-6 sm:p-8 text-white shadow-md">
          <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary-600/30 blur-3xl" />
          <div className="relative flex items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">{t('dashboard.menuReady.title')}</h2>
              <p className="text-base text-neutral-300 mb-5">{t('dashboard.menuReady.text')}</p>
              <Link
                to="/menu"
                className="inline-flex items-center px-4 py-2.5 bg-white text-neutral-900 text-sm font-semibold rounded-lg hover:bg-neutral-100 transition-colors duration-150 shadow-sm"
              >
                <ChefHat className="h-4 w-4 mr-2" />
                {t('dashboard.menuReady.open')}
              </Link>
            </div>
            <ChefHat className="hidden sm:block h-20 w-20 text-white/15" />
          </div>
        </div>
        <AdminDashboardGrid isMobile={isMobile} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PROMINENT MENU MANAGEMENT BANNER */}
      <div className="relative overflow-hidden bg-neutral-900 rounded-2xl p-6 sm:p-8 text-white shadow-md">
        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary-600/30 blur-3xl" />
        <div className="relative flex items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight mb-2">{t('dashboard.menuReady.title')}</h2>
            <p className="text-base text-neutral-300 mb-5">{t('dashboard.menuReady.text')}</p>
            <Link
              to="/menu"
              className="inline-flex items-center px-4 py-2.5 bg-white text-neutral-900 text-sm font-semibold rounded-lg hover:bg-neutral-100 transition-colors duration-150 shadow-sm"
            >
              <ChefHat className="h-4 w-4 mr-2" />
              {t('dashboard.menuReady.open')}
            </Link>
          </div>
          <ChefHat className="hidden sm:block h-20 w-20 text-white/15" />
        </div>
      </div>

      {/* Header */}
      <div>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-semibold tracking-tight text-neutral-900"
        >
          {t('dashboard.welcomeBack')}, {user?.name?.split(' ')[0]}! 👋
        </motion.h1>
        <p className="text-neutral-600 mt-1">
          {t('dashboard.todaysOverview')}
        </p>
      </div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
      >
        <StatsCard
          title={t('dashboard.totalSales')}
          value={formatKyrgyzCurrency(221600)}
          icon={DollarSign}
          color="success"
        />
        <StatsCard
          title={t('dashboard.activeTables')}
          value="8/12"
          change="+2"
          changeType="positive"
          icon={Users}
          color="primary"
        />
        <StatsCard
          title={t('dashboard.totalOrders')}
          value="47"
          icon={ShoppingBag}
          color="warning"
        />
        <StatsCard
          title={t('common.avgServiceTime')}
          value={`18 ${t('common.minutes')}`}
          icon={Clock}
          color="danger"
        />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            {t('dashboard.quickActions')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <QuickActionCard
              title={t('dashboard.newOrder')}
              description={t('dashboard.newOrderDesc')}
              icon={ShoppingBag}
              onClick={() => {}}
              color="primary"
            />
            <QuickActionCard
              title={t('dashboard.kitchenDisplay')}
              description={t('dashboard.kitchenDisplayDesc')}
              icon={ChefHat}
              onClick={() => {}}
              color="success"
            />
            <QuickActionCard
              title={t('dashboard.tableManagement')}
              description={t('dashboard.tableManagementDesc')}
              icon={Users}
              onClick={() => {}}
              color="warning"
            />
            <QuickActionCard
              title={t('dashboard.restaurantAnalytics')}
              description={t('dashboard.restaurantAnalyticsDesc')}
              icon={TrendingUp}
              onClick={() => {}}
              color="danger"
            />
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            {t('dashboard.recentActivity')}
          </h2>
          <div className="bg-white rounded-xl border border-neutral-200">
            <div className="px-4 py-3 border-b border-neutral-200">
              <h3 className="text-sm font-medium text-neutral-700">
                {t('dashboard.liveRestaurantUpdates')}
              </h3>
            </div>
            <div className="divide-y divide-neutral-100">
              <ActivityItem
                title={t('dashboard.orderCompleted', 'Заказ #{{number}} завершен', { number: 127 })}
                description={t('orders.description', { table: 8, amount: 3520 })}
                time={`2 ${t('dashboard.minAgo')}`}
                type="order"
              />
              <ActivityItem
                title={t('dashboard.tableOccupied', 'Столик {{number}} занят', { number: 4 })}
                description={`${t('dashboard.partyOf', 'Компания из {{count}} человек', { count: 3 })} - ${t('dashboard.waiter')}: ${t('system.defaultUser')}`}
                time={`5 ${t('dashboard.minAgo')}`}
                type="table"
              />
              <ActivityItem
                title={t('dashboard.paymentReceived')}
                description={t('orders.description', { table: 12, amount: 6980 })}
                time={`8 ${t('dashboard.minAgo')}`}
                type="payment"
              />
              <ActivityItem
                title={t('dashboard.orderReady', 'Заказ #{{number}} готов', { number: 125 })}
                description={`Столик 6 - ${t('dashboard.readyForPickup')}`}
                time={`12 ${t('dashboard.minAgo')}`}
                type="order"
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-warning-50 border border-warning-200 rounded-xl p-4"
      >
        <div className="flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-warning-600" />
          <div>
            <h3 className="text-sm font-medium text-warning-800">
              {t('common.inventoryAlert')}
            </h3>
            <p className="text-sm text-warning-700">
              {t('common.inventoryAlertDesc')}
            </p>
          </div>
        </div>
      </motion.div>
      
      {/* Partnership footer. The super-admin case returned far above, so this
          component only ever renders for restaurant staff — the role guard that
          used to wrap this was always true, and the matching super-admin block
          below it could never render at all. */}
      {(
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center py-6 border-t border-neutral-200"
        >
          <p className="text-sm text-neutral-600">
            {t('dashboard.network.proudly')} <span className="font-semibold text-amber-600">{t('dashboard.network.name')}</span>
          </p>
          <p className="text-xs text-neutral-500 mt-1">
            {t('dashboard.network.tagline')}
          </p>
        </motion.div>
      )}
      
    </div>
  );
};